import { motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { Container, Overline, SectionHeading, Reveal } from "@/components/primitives";
import { LuxeLink } from "@/components/LuxeLink";
import { IMAGES } from "@/lib/images";

const EASE = [0.16, 1, 0.3, 1];

const HOST_VALUE = [
  { n: "01", t: "More Revenue", d: "Monetize additional guest services." },
  { n: "02", t: "Better Guest Experience", d: "Organize more of the guest's vacation from one place." },
  { n: "03", t: "More Control", d: "Keep the guest relationship connected to the villa." },
  { n: "04", t: "Zero Development Cost", d: "The Guest Portal is provided in comodato gratuito to participating villas." },
];

const BEFORE = ["Guest books villa", "Guest needs a transfer", "Searches online", "Books elsewhere", "Restaurants booked elsewhere", "Boat & experiences elsewhere", "Revenue leaves the villa ecosystem"];
const AFTER = ["Guest books villa", "Receives the VillaFlow Guest Portal", "Discovers services & experiences", "Books through the villa experience", "Host manages the guest journey", "Additional revenue stays in the ecosystem", "The booking becomes more valuable"];

function Bar({ label, value, heightPct, highlight }) {
  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <div className="relative w-full h-64 md:h-80 flex items-end justify-center">
        <motion.div
          initial={{ height: 0 }}
          whileInView={{ height: `${heightPct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: EASE }}
          className={`w-20 md:w-28 ${highlight ? "bg-gold" : "bg-navy-700 border border-ivory/15"} flex items-start justify-center pt-4`}
        >
          <span className={`font-serif text-2xl md:text-3xl ${highlight ? "text-navy" : "text-ivory"}`}>{value}</span>
        </motion.div>
      </div>
      <span className="text-[11px] tracking-[0.2em] uppercase text-ivory/50">{label}</span>
    </div>
  );
}

export default function CaseStudy() {
  return (
    <section className="relative py-28 md:py-40 bg-navy-800 border-y border-ivory/10" data-testid="case-study">
      <Container>
        {/* Positioning headline */}
        <div className="max-w-4xl">
          <Reveal>
            <Overline>Flagship Case Study · Proof of Value</Overline>
            <SectionHeading className="mt-8">
              Stop chasing only new bookings. Start maximizing the bookings you already have.
            </SectionHeading>
            <p className="mt-8 text-ivory/65 text-lg font-light leading-relaxed">
              Don't just fill your villa — maximize every booking. A guest's value doesn't end when the
              stay is paid. VillaFlow helps hosts organise and monetise the whole vacation through the
              <span className="text-ivory"> VillaFlow Guest Portal.</span>
            </p>
          </Reveal>
        </div>

        {/* Case study header */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={IMAGES.loungeInterior} alt="La Balena Blu Luxury Villa" className="w-full h-full object-cover" />
              <span className="absolute top-4 left-4 bg-navy-900/80 backdrop-blur text-gold text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border border-gold/40">VillaFlow Flagship Case Study</span>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <span className="text-[11px] tracking-[0.3em] uppercase text-gold">Real Villa. Real Guests. Real Revenue.</span>
            <h3 className="mt-5 font-serif font-light text-3xl md:text-4xl text-ivory leading-tight">
              How VillaFlow increased additional service revenue at La Balena Blu Luxury Villa.
            </h3>
            <p className="mt-6 text-ivory/60 font-light leading-relaxed text-sm md:text-base">
              La Balena Blu is where the VillaFlow model was tested and validated in the real world — a
              villa that stopped treating the booking as the end of the sale, and started treating it as
              the beginning of the guest experience.
            </p>
          </Reveal>
        </div>

        {/* Numbers + chart */}
        <div className="mt-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center border border-ivory/10 bg-navy-900 p-8 md:p-14">
          <Reveal>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-ivory/45">August 2025</span>
                <p className="mt-2 font-serif text-4xl md:text-5xl text-ivory/80">€6,000<span className="text-2xl">+</span></p>
                <p className="mt-1 text-ivory/40 text-xs">Without the Guest Portal</p>
              </div>
              <div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold">August 2026</span>
                <p className="mt-2 font-serif text-4xl md:text-5xl text-gold">€15,000<span className="text-2xl">+</span></p>
                <p className="mt-1 text-ivory/40 text-xs">With the Guest Portal</p>
              </div>
            </div>
            <div className="gold-rule my-8 max-w-[140px]" />
            <div className="flex items-center gap-3">
              <TrendingUp className="text-gold" size={22} />
              <span className="font-serif text-4xl md:text-5xl text-ivory">+150%<span className="text-gold">+</span></span>
            </div>
            <p className="mt-2 text-ivory/55 text-sm font-light">
              A minimum increase of more than <span className="text-ivory">150%</span> in additional service
              revenue — over <span className="text-ivory">€9,000+</span> in absolute terms, versus August 2025.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="text-[11px] tracking-[0.2em] uppercase text-ivory/45 mb-8 text-center">Additional Service Revenue — August</p>
            <div className="flex items-end justify-center gap-10">
              <Bar label="2025" value="€6K" heightPct={40} />
              <div className="pb-24 text-center">
                <span className="font-serif text-2xl md:text-3xl text-gold">+150%+</span>
              </div>
              <Bar label="2026" value="€15K+" heightPct={100} highlight />
            </div>
          </Reveal>
        </div>

        {/* Guest Portal flow */}
        <Reveal>
          <div className="mt-16 text-center">
            <p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-6">The VillaFlow Guest Portal</p>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {["Stay", "Services", "Experiences", "Concierge"].map((s, i, arr) => (
                <div key={s} className="flex items-center gap-3 md:gap-4">
                  <span className="font-serif text-xl md:text-2xl text-ivory border border-ivory/15 px-6 py-3">{s}</span>
                  {i < arr.length - 1 && <ArrowRight size={16} className="text-gold/60" />}
                </div>
              ))}
            </div>
            <p className="mt-8 text-ivory/60 font-light max-w-2xl mx-auto">
              The Guest Portal turns a simple accommodation booking into a complete vacation experience —
              provided <span className="text-ivory">at no development cost</span> to participating villas.
              No platform to build. Just activate the experience.
            </p>
          </div>
        </Reveal>

        {/* Before / After */}
        <div className="mt-20 grid md:grid-cols-2 gap-px bg-ivory/10 border border-ivory/10">
          <Reveal>
            <div className="bg-navy-900 p-8 md:p-10 h-full">
              <span className="text-[11px] tracking-[0.2em] uppercase text-ivory/40">Before VillaFlow</span>
              <ul className="mt-6 space-y-3">
                {BEFORE.map((b) => <li key={b} className="text-ivory/55 text-sm font-light flex gap-3"><span className="text-ivory/25">—</span>{b}</li>)}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-navy-900 p-8 md:p-10 h-full border-t-2 border-gold/40">
              <span className="text-[11px] tracking-[0.2em] uppercase text-gold">With VillaFlow</span>
              <ul className="mt-6 space-y-3">
                {AFTER.map((a) => <li key={a} className="text-ivory/75 text-sm font-light flex gap-3"><span className="text-gold">·</span>{a}</li>)}
              </ul>
            </div>
          </Reveal>
        </div>

        {/* Host value */}
        <div className="mt-20">
          <Reveal><p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-8">What VillaFlow gives the host</p></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory/10 border border-ivory/10">
            {HOST_VALUE.map((h, i) => (
              <Reveal key={h.n} delay={(i % 4) * 0.06}>
                <div className="bg-navy-900 p-8 h-full">
                  <span className="font-serif text-gold/40 text-3xl">{h.n}</span>
                  <h4 className="font-serif text-xl text-ivory mt-3">{h.t}</h4>
                  <p className="mt-2 text-ivory/50 text-sm font-light leading-relaxed">{h.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Reveal>
          <div className="mt-24 border border-gold/25 px-8 py-16 md:py-20 text-center">
            <p className="font-serif font-light text-3xl md:text-5xl text-ivory tracking-tight">What could your villa generate?</p>
            <p className="mt-5 text-ivory/55 font-light max-w-xl mx-auto">
              You don't always need more guests. Sometimes you need to create more value from the guests
              you already have. The booking is yours — the experience should be yours too.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <LuxeLink to="/apply" variant="solid" data-testid="case-activate-cta">Activate VillaFlow</LuxeLink>
              <LuxeLink to="/private-collection" variant="outline" data-testid="case-how-cta">See How It Works</LuxeLink>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
